import React, { useState, useEffect, useCallback, memo } from 'react';

import { Paper, Table, TableBody, TablePagination } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import CreateGoalPanel from 'components/MeshList/goals/CreateGoalPanel';
import NoDataComponent from 'components/NoDataComponent';
import { addTaggedEmployeesUpdateMacrosDeleteMentions } from 'components/hooks/useTaskReducer';
import _ from 'lodash';
import { getPrimaryPod } from 'models/employee';
import Task1 from 'models/task1';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { selectOrgData } from 'selectors/orgData';
import styled, { css } from 'styled-components';

import { EMPTY_GROUP_TEXT, SEARCH_TAG_TYPES, TASK_TYPES_2 } from 'constants/ProjectConstants';
import { convertDateToString2 } from 'constants/helper';

import { getApiBodyFromEditorStateAndMedia, doesEditorStateContainContent } from 'services/helpers/post';
import { clearTeamsAndRemovedTeam, isAnyOwnerPresent } from 'services/helpers/task';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';
import { createOrUpdateTask, deleteTask } from 'services/task';

import { NoDataComponentTeamHub } from './NoDataComponent';
import TableHeader from './TableHeader';
import TaskHeadingRow from './TaskHeadingRow';
import TaskRowEditView1 from './TaskRowEditView1';
import TaskRowView from './TaskRowView';
import { updateOrderByAction } from './orderByReducer';
import { GOAL_PROGRESS_SORT_OPTION, LAST_ACTIVITY_DATE_SORT_OPTION, TEXT_SORT_OPTION } from './sortDropdownOptions';
import { ADD_NEW_TASK, ENABLE_EDITING, UPDATE_TASK, REMOVE_ITEM } from './taskReducer';

function TeamTasksListView({
  className,
  durationKey,
  tagId,
  tagType,
  employee, // logged in employee is passed here, passing employee to make this component reusable for any employee, used when adding a new task, making sure an employee is selected and we can show his teams on the hashtag page
  style,
  activeFilters,
  statsData,
  isSectionVisible,
  updateTasksLoading,
  hasCardViewDataChanged,
  updateIsTasksDataChanged,
  refreshData,
  type,
  selectedTeam,
  rowsPerPageOptions = [10, 25, 50],
  defaultRowPerPage = 25,
  startTS,
  endTS,
  isOpenCreateGoalPanel,
  setIsOpenCreateGoalPanel,
  isCreateNewTaskInProgress = false,
  setIsCreateNewTaskInProgress = null,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  order,
  orderBy,
  orderByDispatch,
  loading,
  setLoading,
  totalTasks,
  setTotalTasks,
  tasks,
  tasksDispatch,
  getData = () => {},
  isMSTeamsView = false,
}) {
  const orgData = useSelector(selectOrgData, shallowEqual);

  const [groupedTasks, setGroupedTasks] = useState({});
  const dispatch = useDispatch();

  const userTeams = employee.teams;

  const [defaultCreateTask, setDefaultCreateTask] = useState({});

  useEffect(() => {
    if (!tagId || !tagType || !type) return;
    getData && getData();
  }, [tagId, durationKey, type, order, orderBy, page, rowsPerPage, startTS, endTS]);

  useEffect(() => {
    if (!tagId || !tagType || !type) return;
    if (loading) return;
    getData(true);
  }, [JSON.stringify(activeFilters)]);

  useEffect(() => {
    setGroupedTasks(groupTasks(tasks));
  }, [tasks]);

  useEffect(() => {
    if (loading || !refreshData) return;
    getData(true);
  }, [refreshData]);

  useEffect(() => {
    if (isSectionVisible && hasCardViewDataChanged) getData(true);
  }, [isSectionVisible, hasCardViewDataChanged]);

  const groupTasks = (tasks) =>
    tasks?.reduce((r, a, idx) => {
      let value;
      switch (orderBy) {
        case 'STATUS':
          value = a.completionDate ? 'Completed' : 'Ongoing';
          break;
        case 'TAGS':
          if (type !== TASK_TYPES_2.goal) {
            value = a.getFirstTag() && a.getFirstTag().name ? a.getFirstTag().name : EMPTY_GROUP_TEXT;
          } else {
            value = a.subObjective && a.subObjective.name ? a.subObjective.name : EMPTY_GROUP_TEXT;
          }
          break;
        case 'ACTIVE_CONTENT':
          value = a.dueDate ? convertDateToString2(a.dueDate) : EMPTY_GROUP_TEXT;
          break;
        case 'LAST_EDITED_BY':
          value = a.lastEditedBy ? a.lastEditedBy.fullName : EMPTY_GROUP_TEXT;
          break;
        case 'OWNER': {
          const primaryPodName = a.toIds && a.toIds[0] ? getPrimaryPod.call(a.toIds[0]).name : '';
          value =
            a.toIds && isAnyOwnerPresent(a.toIds) && a.toIds[0]
              ? `${a.toIds[0].fullName} ${primaryPodName && `(${primaryPodName})`}`
              : 'Owner Not Assigned';
          break;
        }
        case 'NAME': {
          const primaryPodName = a.toIds && a.toIds[0] ? getPrimaryPod.call(a.toIds[0]).name : '';
          value =
            a.toIds && a.toIds[0]
              ? `${a.toIds[0].fullName} ${primaryPodName && `(${primaryPodName})`}`
              : 'Unassigned Task';
          break;
        }
        case 'TEAM':
          value = a.teamName ? a.teamName : EMPTY_GROUP_TEXT;
          break;
        case 'DEPARTMENT':
          value = a.toIds?.[0]?.department || '';
          break;
        case 'PRIMARY_POD': {
          value = a.toIds && a.toIds[0] ? getPrimaryPod.call(a.toIds[0]).name : '';
          break;
        }
        default:
          value = idx;
      }
      r[value] = [...(r[value] || []), a];
      return r;
    }, {});

  function updateTaskInReducer({ taskId, newTask }) {
    tasksDispatch({ type: UPDATE_TASK, data: { id: newTask.id, key: newTask.key, task: newTask } });
  }

  const removeTaskInReducer = useCallback(
    ({ taskId, key }) => {
      tasksDispatch({ type: REMOVE_ITEM, data: { taskId, key } });
      updateIsTasksDataChanged(true);
    },
    [updateIsTasksDataChanged]
  );

  function deleteTaskHelper(taskId) {}

  function updateTask({ newTask, taskId }) {
    if (!newTask.teamId && (tagType !== SEARCH_TAG_TYPES.tag || type !== TASK_TYPES_2.goal)) {
      dispatch(showErrorSnackbar('Please select a team'));
      return;
    }
    const { text, macros } = getApiBodyFromEditorStateAndMedia(newTask.editorState);
    newTask.text = text;
    if (newTask.text === '' || String(newTask.text).trim().length === 0) {
      dispatch(showErrorSnackbar('Please add some text'));
      return;
    }
    newTask.macros = macros;
    newTask.type = type;
    newTask.isUpdating = true;
    // Already existing task in reducer, setting it to be updating
    updateTaskInReducer({ taskId, newTask });
    const isTaskEmpty = !doesEditorStateContainContent({ editorState: newTask.editorState });
    if (isTaskEmpty) {
      if (!newTask.id) {
        // it's a new task, so just remove from the reducer
        // since we don't have id, in that case we need key
        removeTaskInReducer({ key: newTask.key });
      } else {
        deleteTask({ id: newTask.id })
          .then((res) => {
            dispatch(showSuccessSnackbar(`Successfully deleted ${type.toLowerCase()}`));
            removeTaskInReducer({ taskId });
          })
          .catch((err) => {
            newTask.isUpdating = false;
            newTask.isEditing = true; // if there is an error, we want to remain in an editing state
            updateTaskInReducer({ taskId, newTask });
            dispatch(showErrorSnackbar('There is some problem. Please try again!'));
          });
      }
      return;
    }
    newTask = clearTeamsAndRemovedTeam(addTaggedEmployeesUpdateMacrosDeleteMentions(newTask));
    createOrUpdateTask({
      ...newTask,
      tagType,
      tagId,
      overrideToIds: false,
      orgData,
    })
      .then((task) => {
        if (!newTask.id) {
          // new task - refetch data
          getData();
          dispatch(showSuccessSnackbar(`The ${type.toLowerCase()} was successfully created`));
        } else {
          updateTaskInReducer({ taskId, newTask: task });
        }
        if (typeof updateIsTasksDataChanged === 'function') updateIsTasksDataChanged(true);
      })
      .catch((err) => {
        if (!newTask.id) {
          dispatch(showErrorSnackbar(`Sorry an error occured while creating the ${type.toLowerCase()}`));
        } else {
          dispatch(showErrorSnackbar(`Sorry an error occured while updating the ${type.toLowerCase()}`));
        }
        newTask.isUpdating = false;
        updateTaskInReducer({ taskId, newTask });
      });
  }

  function localUpdatePanelTask({ newTask, taskId }) {
    if (!newTask.teamId && (tagType !== SEARCH_TAG_TYPES.tag || type !== TASK_TYPES_2.goal)) {
      dispatch(showErrorSnackbar('Please select a team'));
      return;
    }
    const { text, macros } = getApiBodyFromEditorStateAndMedia(newTask.editorState);
    newTask.text = text;
    if (newTask.text === '') {
      dispatch(showErrorSnackbar('Please add some text'));
      return;
    }
    newTask.macros = macros;
    newTask.type = type;
    newTask.isUpdating = true;
    // Already existing task in reducer, setting it to be updating
    updateTaskInReducer({ taskId, newTask });
    const isTaskEmpty = !doesEditorStateContainContent({ editorState: newTask.editorState });
    if (isTaskEmpty) {
      if (!newTask.id) {
        // it's a new task, so just remove from the reducer
        // since we don't have id, in that case we need key
        removeTaskInReducer({ key: newTask.key });
      } else {
        deleteTask({ id: newTask.id })
          .then((res) => {
            dispatch(showSuccessSnackbar(`Successfully deleted ${type.toLowerCase()}`));
            removeTaskInReducer({ taskId });
          })
          .catch((err) => {
            newTask.isUpdating = false;
            newTask.isEditing = true; // if there is an error, we want to remain in an editing state
            updateTaskInReducer({ taskId, newTask });
            dispatch(showErrorSnackbar('There is some problem. Please try again!'));
          });
      }
      return;
    }
    newTask = clearTeamsAndRemovedTeam(addTaggedEmployeesUpdateMacrosDeleteMentions(newTask));
    createOrUpdateTask({
      ...newTask,
      tagType,
      tagId,
      overrideToIds: false,
      orgData,
    })
      .then((task) => {
        if (!newTask.id) {
          // new task - refetch data
          getData();
          dispatch(showSuccessSnackbar(`The ${type.toLowerCase()} was successfully created`));
        } else {
          updateTaskInReducer({ taskId, newTask: task });
        }
        if (typeof updateIsTasksDataChanged === 'function') updateIsTasksDataChanged(true);
      })
      .catch((err) => {
        if (!newTask.id) {
          dispatch(showErrorSnackbar(`Sorry an error occured while creating the ${type.toLowerCase()}`));
        } else {
          dispatch(showErrorSnackbar(`Sorry an error occured while updating the ${type.toLowerCase()}`));
        }
        newTask.isUpdating = false;
        updateTaskInReducer({ taskId, newTask });
      });
  }

  function toggleCompletionStatus({ task, taskId, isChecked }) {
    let newTask = { ...task };
    newTask.completionDate = !isChecked ? new Date() : (newTask.completionDate = null);
    updateTaskInReducer({ taskId, newTask });
    // removing this - use the same teamId bind to the task
    // newTask.teamId = teamId;
    newTask = clearTeamsAndRemovedTeam(newTask);
    createOrUpdateTask({ ...newTask, overrideToIds: false, type: TASK_TYPES_2.task })
      .then((task) => {
        updateTaskInReducer({ taskId, newTask: task });
        if (typeof updateIsTasksDataChanged === 'function') updateIsTasksDataChanged(true);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(`Sorry an error occured while updating the ${type.toLowerCase()}`));
        // Reverting back to old
        updateTaskInReducer({ taskId, newTask: task });
      });
  }

  const enableEditing = (taskId) => {
    tasksDispatch({ type: ENABLE_EDITING, data: { taskId } });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    updateOrderByAction({ type: isAsc ? 'desc' : 'asc', field: property })(orderByDispatch);
  };

  function addNewTask() {
    const newEmployee = { ...employee, ownerType: Task1.EMPLOYEE_TYPES.owner };
    tasksDispatch({
      type: ADD_NEW_TASK,
      data: {
        employee: newEmployee,
        // if this is org tag page, set team of task as preferred team of employee || whatever is the first team
        // in case no preferred team
        // for team type page, set team as same that is opened.
        teamId:
          tagType === SEARCH_TAG_TYPES.tag
            ? getPrimaryPod.call(employee)?.id || employee.teams[0]?.id
            : tagType === SEARCH_TAG_TYPES.team
              ? tagId
              : getPrimaryPod.call(employee)?.id || employee.teams[0]?.id,
        teams:
          tagType === SEARCH_TAG_TYPES.tag
            ? [{ uuid: getPrimaryPod.call(employee)?.id || employee.teams[0]?.id }]
            : tagType === SEARCH_TAG_TYPES.team
              ? [{ uuid: tagId }]
              : [{ uuid: getPrimaryPod.call(employee)?.id || employee.teams[0]?.id }],
      },
    });
  }

  const isGoalView = type === TASK_TYPES_2.goal;

  const DISABLE_SORTING_FOR = [TEXT_SORT_OPTION, LAST_ACTIVITY_DATE_SORT_OPTION, GOAL_PROGRESS_SORT_OPTION].map(
    (o) => o.key
  );

  useEffect(() => {
    if (isCreateNewTaskInProgress) {
      addNewTask();
      setIsCreateNewTaskInProgress(false);
    }
  }, [isCreateNewTaskInProgress]);

  return isSectionVisible ? (
    <div className={className}>
      {loading && (
        <>
          <NoDataComponent height="5vh" message="Assembling your team" />
          <div>
            <Skeleton height="8vh" />
            <Skeleton height="3vh" variant="text" />
            <Skeleton height="3vh" variant="text" />
            <Skeleton height="3vh" variant="text" />
            <Skeleton height="3vh" variant="text" />
          </div>
        </>
      )}
      {!loading && _.isEmpty(groupedTasks) && <NoDataComponentTeamHub isGoalView={isGoalView} />}
      {!loading && !_.isEmpty(groupedTasks) && (
        <StyledPaper style={style}>
          <StyledTable $isMSTeamsView={isMSTeamsView} isGoalView={isGoalView} size="small" aria-label="a dense table">
            <TableHeader
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              tagType={tagType}
              type={type}
              orderByDispatch={orderByDispatch}
            />
            {Object.keys(groupedTasks).map((heading, index) => (
              <TableBodyWithBeforeSupport index={index}>
                {orderBy &&
                  !DISABLE_SORTING_FOR.includes(orderBy) &&
                  (groupedTasks[heading].length !== 1 || !groupedTasks[heading][0].isEditing) && (
                    <TaskHeadingRow orderBy={orderBy} text={heading} />
                  )}
                {groupedTasks[heading].map((task) =>
                  task.isEditing ? (
                    <TaskRowEditView1
                      key={`taskListView${task.id}`}
                      taskId={task.id}
                      task={task}
                      updateTaskInReducer={updateTaskInReducer}
                      updateTask={updateTask}
                      removeTaskInReducer={removeTaskInReducer}
                      teamId={task.teamId}
                      tagType={tagType}
                      tagId={tagId}
                      userTeams={userTeams}
                      type={type}
                      employee={employee}
                    />
                  ) : (
                    <TaskRowView
                      key={`taskListView${task.id}`}
                      taskId={task.id}
                      task={task}
                      tasksDispatch={tasksDispatch}
                      enableEditing={enableEditing}
                      updateTaskInReducer={updateTaskInReducer}
                      updateTask={updateTask}
                      toggleCompletionStatus={toggleCompletionStatus}
                      removeTaskInReducer={removeTaskInReducer}
                      tagType={tagType}
                      type={type}
                      employee={employee}
                      setDefaultCreateTask={setDefaultCreateTask}
                      setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
                      isMSTeamsView={isMSTeamsView}
                    />
                  )
                )}
              </TableBodyWithBeforeSupport>
            ))}
            {/* <TableBody>
              <TableRow align="center" key="tableRowAddTask">
                <TableCell colSpan="8" variant="footer" align="center"></TableCell>
              </TableRow>
            </TableBody> */}
          </StyledTable>
          <Footer>
            <div />
            <StyledTablePagination
              component="div"
              count={totalTasks}
              labelDisplayedRows={() => ''} // for hiding page count
              rowsPerPage={rowsPerPage}
              page={page}
              rowsPerPageOptions={rowsPerPageOptions}
              onChangePage={(e, newPage) => setPage(newPage)}
              onChangeRowsPerPage={(event) => {
                const { target } = event;
                setRowsPerPage(target.value);
              }}
            />
          </Footer>
        </StyledPaper>
      )}
      <CreateGoalPanel
        open={isOpenCreateGoalPanel}
        onCancel={() => {
          setIsOpenCreateGoalPanel(false);
        }}
        goalListDispatch={tasksDispatch}
        employee={employee}
        defaultTask={defaultCreateTask}
        source="teampage"
        eventSource="Team Page"
      />
    </div>
  ) : null;
}

export default memo(TeamTasksListView);

const StyledPaper = styled(Paper)`
  overflow-x: scroll;
  background: ${(props) => props.theme.palette.background.default};
  ${(props) => props.theme.breakpoints.up('lg')} {
    overflow-x: visible;
  }
  box-shadow: none;
  position: relative;
  z-index: 0;
`;

const TableBodyWithBeforeSupport = styled(TableBody)`
  ${(props) =>
    props.index === 0 &&
    css`
      &:before {
        line-height: 0.75em;
        content: '.';
        color: ${(props) => props.theme.palette.background.default};
        display: block;
      }
    `}
`;

const StyledTablePagination = styled(TablePagination)`
  .MuiTablePagination-caption {
    padding: 1rem 1.5rem;
  }
  .MuiTablePagination-actions button {
    background: ${(props) => props.theme.palette.background.paper};
    border-radius: 0;
    &:nth-child(2) {
      margin-left: 1rem;
    }
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
  margin-bottom: 2rem;
`;

const StyledTable = styled(Table)`
  table-layout: auto;
  ${(props) =>
    props.$isMSTeamsView &&
    `
      table-layout: fixed;
      width: 100%;
    `}
`;
